import { ChevronDown, GraduationCap } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation } from 'react-router'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'
import type { NavGroup, NavItem } from '../navigation'

interface SidebarProps {
  groups: NavGroup[]
  brand: { name: string; subtitle: string; logoUrl?: string | null; homeHref: string }
  onNavigate?: () => void
  footer?: React.ReactNode
}

function isWithin(pathname: string, item: NavItem): boolean {
  if (item.children) return item.children.some((c) => isWithin(pathname, c))
  return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`)
}

const linkBase =
  'group flex items-center gap-3 rounded-control px-3 py-2 text-[0.8125rem] font-medium transition-colors duration-150'

function NavEntry({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  useTranslation() // re-render on language change; labels come from registries (tAny)
  const { pathname } = useLocation()
  const active = isWithin(pathname, item)
  // Expanded when the user toggled it open, otherwise whenever it contains the active page.
  const [toggled, setToggled] = useState<boolean | null>(null)
  const open = toggled ?? active
  const Icon = item.icon

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            linkBase,
            isActive
              ? 'bg-brand-50 text-brand-700 shadow-[inset_3px_0_0_0_var(--color-brand-600)]'
              : 'text-ink-soft hover:bg-surface-sunken hover:text-ink',
          )
        }
      >
        {Icon ? (
          <Icon
            className="size-[1.125rem] shrink-0 opacity-80 group-aria-[current=page]:opacity-100"
            aria-hidden="true"
          />
        ) : null}
        <span className="truncate">{tAny(item.label)}</span>
      </NavLink>
    )
  }

  const panelId = `nav-${item.to.replace(/\W+/g, '-')}`
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setToggled(!open)}
        className={cn(
          linkBase,
          'w-full',
          active ? 'text-brand-700' : 'text-ink-soft hover:bg-surface-sunken hover:text-ink',
        )}
      >
        {Icon ? <Icon className="size-[1.125rem] shrink-0 opacity-80" aria-hidden="true" /> : null}
        <span className="flex-1 truncate text-left">{tAny(item.label)}</span>
        <ChevronDown
          className={cn('size-4 text-subtle transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      <div id={panelId} hidden={!open} className="mt-0.5 ml-[1.35rem] border-l border-line pl-3">
        {item.children.map((child) => (
          <NavLink
            key={child.to + child.label}
            to={child.to}
            end={child.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'relative flex items-center rounded-control px-3 py-1.5 text-[0.8125rem] transition-colors',
                isActive
                  ? 'font-medium text-brand-700 before:absolute before:top-1/2 before:-left-[0.8rem] before:size-1.5 before:-translate-y-1/2 before:rounded-full before:bg-brand-600'
                  : 'text-muted hover:text-ink',
              )
            }
          >
            {tAny(child.label)}
          </NavLink>
        ))}
      </div>
    </div>
  )
}

export function Sidebar({ groups, brand, onNavigate, footer }: SidebarProps) {
  const { t } = useTranslation()
  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-header shrink-0 items-center gap-3 border-b border-line px-5">
        <Link
          to={brand.homeHref}
          onClick={onNavigate}
          className="flex min-w-0 items-center gap-3 rounded-control"
        >
          <span className="bg-brand-gradient grid size-9 shrink-0 place-items-center overflow-hidden rounded-xl text-white shadow-brand">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt="" className="size-full bg-white object-contain" />
            ) : (
              <GraduationCap className="size-5" aria-hidden="true" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink">{brand.name}</span>
            <span className="block truncate text-2xs font-medium tracking-wide text-muted uppercase">
              {tAny(brand.subtitle)}
            </span>
          </span>
        </Link>
      </div>
      <nav
        aria-label={t('Main navigation')}
        className="flex-1 scrollbar-thin space-y-6 overflow-y-auto px-3 py-5"
      >
        {groups.map((group) => (
          <div key={group.label}>
            <p className="mb-1.5 px-3 text-2xs font-semibold tracking-[0.08em] text-subtle uppercase">
              {tAny(group.label)}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavEntry key={item.to + item.label} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      {footer ? <div className="border-t border-line p-3">{footer}</div> : null}
    </div>
  )
}
